"use client"

/**
 * Greeting banner background — profile menu and inline banner picker share this grid.
 */

import { ProfileMenuSubTrigger } from "@/components/profile-menu-sub-trigger"
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu"
import { SelectionTileGrid } from "@/components/ui/selection-tile-grid"
import { useLoginSession } from "@/hooks/use-login-session"
import {
  setGreetingBannerIllustration,
  useGreetingBannerIllustration,
} from "@/hooks/use-greeting-banner-illustration"
import { identityPickerLabel } from "@/lib/login-session"
import {
  GREETING_BANNER_ILLUSTRATIONS,
  GREETING_BANNER_THUMB_ILLUSTRATION_CLASS,
  greetingBannerIllustrationOption,
  type GreetingBannerIllustrationId,
} from "@/lib/greeting-banner-illustrations"
import { cn } from "@/lib/utils"

function greetingIllustrationLeading(src: string | null) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        className={GREETING_BANNER_THUMB_ILLUSTRATION_CLASS}
      />
    )
  }
  return (
    <span
      aria-hidden
      className="size-full rounded-md bg-[color-mix(in_oklch,var(--brand-color)_10%,var(--card))]"
    />
  )
}

const GREETING_TILE_OPTIONS = GREETING_BANNER_ILLUSTRATIONS.map(option => ({
  value: option.id,
  label: option.label,
  leading: greetingIllustrationLeading(option.src),
}))

export function GreetingBannerBackgroundPickerGrid({
  value,
  onValueChange,
  className,
  showSectionLabel = true,
  sectionLabel = "Greeting background",
}: {
  value: GreetingBannerIllustrationId
  onValueChange: (next: GreetingBannerIllustrationId) => void
  className?: string
  showSectionLabel?: boolean
  sectionLabel?: string
}) {
  return (
    <SelectionTileGrid<GreetingBannerIllustrationId>
      sectionLabel={showSectionLabel ? sectionLabel : undefined}
      className={className}
      labelPlacement="below"
      interaction="button"
      columns={3}
      options={GREETING_TILE_OPTIONS}
      value={value}
      onValueChange={onValueChange}
    />
  )
}

/** Profile submenu and inline popover — wide enough for 3 fixed preview columns. */
export const GREETING_BACKGROUND_PICKER_SHELL_CLASS = "w-[21rem] p-3"

export function GreetingBannerBackgroundMenu() {
  const session = useLoginSession()
  const illustration = useGreetingBannerIllustration()
  const active = greetingBannerIllustrationOption(illustration)
  const roleLabel = identityPickerLabel(session.role)

  return (
    <DropdownMenuSub>
      <ProfileMenuSubTrigger
        iconClassName="fa-light fa-image"
        label="Greeting background"
        trailing={active.label}
      />
      <DropdownMenuSubContent
        className={cn(
          GREETING_BACKGROUND_PICKER_SHELL_CLASS,
          "max-h-[min(32rem,var(--radix-dropdown-menu-content-available-height))] overflow-x-hidden overflow-y-auto",
        )}
      >
        <div
          onPointerDown={event => event.preventDefault()}
          onClick={event => event.stopPropagation()}
        >
          <GreetingBannerBackgroundPickerGrid
            showSectionLabel={false}
            sectionLabel={`Greeting background for ${roleLabel}`}
            value={illustration}
            onValueChange={next => setGreetingBannerIllustration(next)}
          />
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
